import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "qrcode-loading" }
const _hoisted_2 = { class: "qrcode-container" }

import { ref, watch, nextTick, onUnmounted } from 'vue'
  import QRCode from 'qrcode'
  import axios from 'axios'
  import { ElMessage } from 'element-plus'
  import { Loading } from '@element-plus/icons-vue'
  
  // Props & Emits
  type LoginState = 'init' | 'waiting' | 'scanned' | 'confirmed' | 'error'
  
export default /*@__PURE__*/_defineComponent({
  __name: 'WeChatLogin',
  props: {
    visible: Boolean,
    apiBaseUrl: {
      type: String,
      default: 'http://localhost:8001/api/auth/wechat'
    }
  },
  emits: ['update:visible', 'login-success'],
  setup(__props, { emit: __emit }) {

  const props = __props
  // 换成数组形式，不使用泛型
  const emit = __emit
  
  // Reactive State
  const dialogVisible = ref(false)
  const loading = ref(true)
  const qrcodeCanvas = ref<HTMLCanvasElement | null>(null)
  const currentTicket = ref<string | null>(null)
  // 浏览器环境下 setInterval 返回 number
  const timer = ref<number | null>(null)
  
  const loginState = ref<LoginState>('init')
  const statusMessages: Record<LoginState, string> = {
    init: '正在准备二维码...',
    waiting: '请使用微信扫码登录',
    scanned: '扫码成功，请在手机上确认登录',
    confirmed: '登录成功，正在跳转...',
    error: '登录失败，请重试'
  }
  const statusMessage = ref(statusMessages.init)
  const statusClass = ref('')
  
  // 清理定时器
  function clearPolling() {
    if (timer.value !== null) {
      window.clearInterval(timer.value)
      timer.value = null
    }
  }
  onUnmounted(clearPolling)
  
  // 同步 dialogVisible 与父组件 v-model
  watch(() => props.visible, (v) => {
    dialogVisible.value = v
    if (v) resetState()
    else clearPolling()
  })
  watch(dialogVisible, (v) => {
    if (!v) emit('update:visible', false)
  })
  
  // 重置所有状态
  function resetState() {
    loading.value = true
    loginState.value = 'init'
    statusMessage.value = statusMessages.init
    statusClass.value = ''
    currentTicket.value = null
  }
  
  // 更新登录状态
  function updateStatus(state: LoginState) {
    loginState.value = state
    statusMessage.value = statusMessages[state]
    statusClass.value =
      state === 'error' ? 'status-error' :
      state === 'confirmed' ? 'status-success' : ''
  }
  
  // 对话框打开后生成二维码
  function handleDialogOpened() {
    nextTick(generateQRCode)
  }
  
  // 在关闭前调用
  function handleClose(done: () => void) {
    clearPolling()
    done()
  }
  
  // 关闭对话框
  function closeDialog() {
    clearPolling()
    dialogVisible.value = false
  }
  
  // 生成二维码并开始轮询
  async function generateQRCode() {
    try {
      if (!qrcodeCanvas.value) throw new Error('二维码容器未挂载')
  
      // 设置 canvas 大小
      qrcodeCanvas.value.width = 300
      qrcodeCanvas.value.height = 300
  
      // 请求二维码数据
      const res = await axios.get(`${props.apiBaseUrl}/qrcode`)
      const { ticket, qrcodeUrl } = res.data.data
      currentTicket.value = ticket
  
      // 清空旧绘制
      const ctx = qrcodeCanvas.value.getContext('2d')
      if (ctx) ctx.clearRect(0, 0, 300, 300)
  
      // 打印 URL
      console.log('准备发送请求到二维码 URL:', qrcodeUrl)
  
      // 绘制二维码
      await QRCode.toCanvas(qrcodeCanvas.value, qrcodeUrl, {
        width: 280,
        margin: 2,
        errorCorrectionLevel: 'H',
        color: { dark: '#000000', light: '#ffffff' }
      })
  
      updateStatus('waiting')
      loading.value = false
      startPolling(ticket)
    } catch (err) {
      console.error('生成二维码失败:', err)
      updateStatus('error')
      loading.value = false
      ElMessage.error('生成二维码失败，请重试')
      dialogVisible.value = false
    }
  }
  
  // 轮询扫码/确认状态
  function startPolling(ticket: string) {
    clearPolling()
    timer.value = window.setInterval(async () => {
      try {
        const res = await axios.get(`${props.apiBaseUrl}/check?ticket=${ticket}`)
        const status = res.data.data.status
        if (status === 'SCANNED') {
          updateStatus('scanned')
        } else if (status === 'CONFIRMED') {
          const { token, userInfo } = res.data.data
          handleLoginSuccess(token, userInfo)
        } else if (status === 'EXPIRED') {
          ElMessage.warning('二维码已过期')
          updateStatus('error')
          dialogVisible.value = false
        }
      } catch {
        // 可选：处理请求失败或重试逻辑
      }
    }, 2000)
  }
  
  // 处理登录成功
  function handleLoginSuccess(token: string, userInfo: any) {
    clearPolling()
    updateStatus('confirmed')
    localStorage.setItem('jwt_token', token)
    setTimeout(() => {
      emit('login-success', userInfo)
      dialogVisible.value = false
    }, 1000)
  }
  
  // 用户点击“确认登录”
  async function handleConfirmLogin() {
    if (!currentTicket.value) return
    try {
      await axios.post(`${props.apiBaseUrl}/confirm`, { ticket: currentTicket.value })
      updateStatus('confirmed')
    } catch {
      ElMessage.error('确认登录失败')
      updateStatus('error')
    }
  }
  
return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    modelValue: dialogVisible.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dialogVisible).value = $event)),
    title: "微信扫码登录",
    width: "350px",
    "before-close": handleClose,
    onOpened: handleDialogOpened
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_el_button, { onClick: closeDialog }, {
        default: _withCtx(() => _cache[2] || (_cache[2] = [
          _createTextVNode("取消")
        ])),
        _: 1
      }),
      (loginState.value === 'scanned')
        ? (_openBlock(), _createBlock(_component_el_button, {
            key: 0,
            type: "primary",
            onClick: handleConfirmLogin
          }, {
            default: _withCtx(() => _cache[3] || (_cache[3] = [
              _createTextVNode(" 确认登录 ")
            ])),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      _withDirectives(_createElementVNode("div", _hoisted_1, [
        _createVNode(_component_el_icon, {
          class: "is-loading",
          size: "24"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Loading))
          ]),
          _: 1
        }),
        _cache[1] || (_cache[1] = _createElementVNode("span", { class: "loading-text" }, "正在生成二维码...", -1))
      ], 512), [
        [_vShow, loading.value]
      ]),
      _withDirectives(_createElementVNode("div", null, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("canvas", {
            ref_key: "qrcodeCanvas",
            ref: qrcodeCanvas,
            class: "qrcode-canvas"
          }, null, 512)
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(["status-message", statusClass.value])
        }, _toDisplayString(statusMessage.value), 3)
      ], 512), [
        [_vShow, !loading.value]
      ])
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})